import { useVehicleCatalog } from "../../../lib/api";
import { vehicleImageFor } from "@packs/automotive/web/lib/msdVehicles";
import { labelForStage } from "../../../lib/groupEventLabels";
import { documentKindLabel, effectiveMediaType } from "../../../components/chat/chatUtils";
import { Icon } from "../../../components/msd/Icon";
import { cx } from "../../../components/msd/ui";

// The pieces the Chat history list and record view share, rebuilt from the
// prototype's .hx-* rules.

const asDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "3 Oct 2026", or "" when the value is missing or not a date.
export function dayLabel(v, withYear = true) {
  const d = asDate(v);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });
}

// "2 Oct to 3 Oct 2026": from when the group was opened to when the vehicle was
// delivered (or, failing that, when the WhatsApp group was deleted). A group
// with no end yet reads "Since 2 Oct 2026".
export function servicePeriod(g) {
  const start = asDate(g?.["Created At"]);
  const end = asDate(g?.["Actual Delivered At"]) || asDate(g?.["WA Deleted At"]);
  if (!start) return "";
  if (!end) return `Since ${dayLabel(start)}`;
  return `${dayLabel(start, start.getFullYear() !== end.getFullYear())} to ${dayLabel(end)}`;
}

// The vehicle's catalog photo, else a plain car tile. It asks for the catalog
// itself so the page only loads it once there is a vehicle to draw.
export function VehiclePic({ group, hero = false, className }) {
  const { data } = useVehicleCatalog();
  const model = vehicleImageFor(group, data);
  const src = hero ? model?.hero_url || model?.thumb_url : model?.thumb_url || model?.hero_url;
  if (src) return <img src={src} alt="" loading="lazy" className={cx("shrink-0 bg-[#EEF0F4] object-cover", className)} />;
  return (
    <span className={cx("flex shrink-0 items-center justify-center bg-msd-soft text-msd-faint", className)} aria-hidden="true">
      <Icon name="car" size={hero ? 40 : 24} stroke={1.6} />
    </span>
  );
}

const MEDIA_TYPES = new Set(["image", "video", "document", "audio"]);

export const mediaRowsOf = (messages) =>
  messages.filter((m) => MEDIA_TYPES.has(m.message_type) && (m.media_url || m.message_text));

const titleCase = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");

// .hx-file — one shared photo or document; opens in a new tab.
export function FileTile({ msg }) {
  const url = msg.media_url || msg.message_text;
  const type = effectiveMediaType(msg.message_type, url);
  const time = [dayLabel(msg.created_at, false), asDate(msg.created_at)?.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })]
    .filter(Boolean)
    .join(", ");
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-w-0 flex-col overflow-hidden rounded-[14px] bg-msd-surface text-msd-ink no-underline shadow-msd-e1 hover:shadow-msd-e2"
    >
      {type === "image" ? (
        <img src={url} alt="" loading="lazy" className="h-[110px] w-full bg-msd-soft object-cover" />
      ) : (
        <span className="flex h-[110px] w-full flex-col items-center justify-center gap-1 bg-msd-soft text-[#F04438]">
          <Icon name={type === "document" ? "doc" : "clip"} size={26} stroke={1.8} />
          <em className="text-[11px] font-bold uppercase not-italic tracking-[.06em] text-msd-muted">
            {type === "document" ? documentKindLabel(url) : type}
          </em>
        </span>
      )}
      <span className="flex flex-col px-3 py-[10px]">
        <b className="truncate text-[13.5px] font-semibold">{labelForStage(msg.stage) || titleCase(type) || "File"}</b>
        {time ? <span className="text-[13px] text-msd-muted">{time}</span> : null}
      </span>
    </a>
  );
}

// .hx-key — one row of a right-hand card: a mark, a title with a line under it,
// and something short on the right.
export function KeyRow({ mark, title, sub, aside, tight = false }) {
  return (
    <div className={cx("flex items-center gap-3 border-t border-msd-line text-[14.5px] first:border-t-0", tight ? "py-[9px]" : "py-[11px]")}>
      {mark}
      <span className="min-w-0 grow">
        <b className={cx("block break-words", tight ? "font-medium" : "font-semibold")}>{title}</b>
        {sub ? <span className="block break-words text-[13px] text-msd-muted">{sub}</span> : null}
      </span>
      {aside ? <span className="whitespace-nowrap text-[13px] text-msd-muted">{aside}</span> : null}
    </div>
  );
}

// .gico — the coloured square a key moment's icon sits in.
export const KeyIcon = ({ icon, color }) => (
  <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg text-white" style={{ background: color }}>
    <Icon name={icon} size={15} stroke={2.2} />
  </span>
);
